import LoadingComponent from "../../../../../components/LoadingComponent.jsx";
import React, {useEffect, useMemo, useState} from "react";
import useAuthorityConfiguration from "../../../../../api/utilities/useAuthorityConfiguration.js";
import {ErpCalculationApi} from "../../../../../api/authority/generated/index";
import {useTranslation} from "react-i18next";
import SeeMore from "../../../../../components/SeeMore.jsx";
import toast from "react-hot-toast";
import {useErrorTranslation} from "../../../../../api/utilities/translateErrorMessage.js";
import Toggle from "../../../../../components/Toggle.jsx";
import {useRenderCurrencyValue, useRenderNumber} from "../../../../../api/utilities/useValueRenderers.js";
import useUserSettings from "../../../../../utils/useUserSettings.js";
import DataGrid2 from "../../../../../components/datagrid/DataGrid2.jsx";
import {snapshotHasPublishedDays, snapshotRowsForPublishedDays} from "../../../../../utils/reportingSnapshotAvailability.js";
import ReportingSnapshotStatus from "../../../../admin/ReportingSnapshotStatus.jsx";

const Mode = {
    Turnover: 'turnover',
    Amount: 'amount',
};

export default function DailyTopProductPerformance({title, day, project}) {
    let {t} = useTranslation();
    let {et} = useErrorTranslation();

    let [data, setData] = useState(null);
    let workerConfig = useAuthorityConfiguration();
    let renderCurrencyValue = useRenderCurrencyValue();
    let renderNumber = useRenderNumber();
    let dayKey = day.format('YYYY-MM-DD');

    let [userSettings] = useUserSettings();

    useEffect(() => {
        let active = true;
        setData(null);

        new ErpCalculationApi(workerConfig)
            .dailyProductPerformance({
                projectId: project.id,
                productPerformanceDayInput: {
                    day: dayKey,
                    limit: 20,
                }
            })
            .then((nextData) => {
                if (!active) {
                    return;
                }

                setData(nextData);
            })
            .catch(e => {
                if (!active) {
                    return;
                }

                et(e).then((message) => { if (message) toast.error(message); });
            });

        return () => {
            active = false;
        };
    }, [dayKey, project.id]);

    let [mode, setMode] = useState(Mode.Amount);

    let productPublication = data?.productPublication;
    let productsPublished = snapshotHasPublishedDays(productPublication);
    let dataSource = useMemo(() => {
        let rows = ((mode === Mode.Turnover) ? (data?.topProductsByTurnoverGross) : (data?.topProductsByAmountGross)) ?? [];
        return snapshotRowsForPublishedDays(rows, productPublication);
    }, [data, mode, productPublication]);

    let columns = useMemo(() => [
        {
            name: 'productName',
            header: t('orders.product-name'),
            defaultFlex: 2,
            sortable: false,
        },
        {
            name: 'soldAmount',
            header: t('orders.amount'),
            defaultFlex: 1,
            textAlign: 'end',
            sortable: false,
            render: (input) => renderNumber(
                input.data.amount,
                true,
            ),
        },
        {
            name: 'turnover',
            header: t('dashboard.turnover'),
            defaultFlex: 2,
            textAlign: 'end',
            minWidth: 100,
            sortable: false,
            render: (input) => renderCurrencyValue(
                input.data.turnover,
                true,
            ),
        },
    ], [userSettings]);

    // noinspection JSUnresolvedReference
    return (
        <LoadingComponent
            loading={data === null}
            className={'col-span-12 sm:col-span-6 text-slate-600 dark:text-slate-400'}
        >
            <div
                className="comp__roundedBox reporting-surface rounded-lg inline-flex flex-col w-full text-sm bg-white dark:bg-slate-800 shadow-lg border border-slate-200 dark:border-slate-700">
                <div
                    className={'px-5 py-4 border-b border-slate-100 dark:border-slate-700 flex justify-between'}>
                    <h2 className={'font-semibold text-slate-800 dark:text-slate-100'}>
                        {title}
                    </h2>
                    <div className={'flex gap-2'}>
                        {t('orders.turnover')}
                        <Toggle enabled={mode === Mode.Amount} setEnabled={() => {
                            setMode(mode === Mode.Amount ? Mode.Turnover : Mode.Amount);
                        }}/>
                        {t('orders.amount')}
                    </div>
                </div>
                <div className={'p-3'}>
                    <ReportingSnapshotStatus snapshots={productPublication}/>
                    {productsPublished
                        ? <SeeMore heightClass={'h-32'} active={dataSource.length > 5}>
                            <DataGrid2 columns={columns} dataSource={dataSource}/>
                        </SeeMore>
                        : <div role="status" className="text-sm text-slate-500 dark:text-slate-400">{t("reporting_lab.snapshot.no_published_days_short", {defaultValue: "Bez publikovaných dnů"})}</div>}
                </div>
            </div>
        </LoadingComponent>
    );
}
